import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import { createVisemeScheduler, CROSSFADE_MS } from "../utils/visemeScheduler";

/**
 * Player de avatar barato: 1 foto-base fixa + 1 patch pequeno da região da boca,
 * trocado entre as classes de viseme em sincronia com um áudio (Azure Neural TTS).
 * Expõe a mesma superfície imperativa que `<video>` já oferecia (`play()`, `pause()`,
 * `currentTime`, `playbackRate`, `paused`) para que o código que já controla a
 * entrevista (tour, replay, velocidade) não precise mudar.
 *
 * `mouthAssets` é gerado pelo pipeline de extração por avatar (Fase 1):
 * { base, patchRect: {left, top, width, height}, closed, closedNarrow, consonant,
 *   l, fv, smile, openA, roundedO, roundedU }
 */
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const BaseImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
`;

const MouthPatch = styled.img`
  position: absolute;
  left: ${(p) => p.$left}px;
  top: ${(p) => p.$top}px;
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  opacity: ${(p) => p.$opacity};
  transition: opacity ${CROSSFADE_MS}ms ease-in-out;
  pointer-events: none;
  user-select: none;
`;

const HiddenAudio = styled.audio`
  display: none;
`;

const AvatarVisemePlayer = forwardRef(function AvatarVisemePlayer(
  {
    avatarId,
    mouthAssets,
    audioSrc,
    visemes,
    autoPlay = false,
    preload = "auto",
    onLoadStart,
    onCanPlay,
    onLoadedData,
    onWaiting,
    onError,
    onPlay,
    onEnded,
  },
  ref
) {
  const audioRef = useRef(null);
  const [layerAActive, setLayerAActive] = useState(true);
  const [patchA, setPatchA] = useState(mouthAssets?.closed || mouthAssets?.base || null);
  const [patchB, setPatchB] = useState(null);

  useImperativeHandle(ref, () => {
    const handle = {
      play: () => audioRef.current?.play() ?? Promise.resolve(),
      pause: () => audioRef.current?.pause(),
      get currentTime() { return audioRef.current?.currentTime ?? 0; },
      set currentTime(value) { if (audioRef.current) audioRef.current.currentTime = value; },
      get paused() { return audioRef.current?.paused ?? true; },
    };
    Object.defineProperty(handle, "playbackRate", {
      enumerable: true,
      get() { return audioRef.current?.playbackRate ?? 1; },
      set(value) { if (audioRef.current) audioRef.current.playbackRate = value; },
    });
    return handle;
  }, []);

  // Reseta a boca pro estado fechado sempre que troca de fala/avatar, evitando
  // que o último formato da fala anterior fique "congelado" na tela.
  useEffect(() => {
    setPatchA(mouthAssets?.closed || mouthAssets?.base || null);
    setPatchB(null);
    setLayerAActive(true);
  }, [audioSrc, mouthAssets]);

  useEffect(() => {
    const applyViseme = (name) => {
      const src = mouthAssets?.[name] || mouthAssets?.closed;
      if (!src) return;
      setLayerAActive((wasA) => {
        if (wasA) {
          setPatchB(src);
        } else {
          setPatchA(src);
        }
        return !wasA;
      });
    };

    const scheduler = createVisemeScheduler({
      getCurrentTimeMs: () => (audioRef.current?.currentTime || 0) * 1000,
      visemes,
      onVisemeChange: applyViseme,
    });

    const audioEl = audioRef.current;
    const handlePlay = (e) => { scheduler.start(); onPlay?.(e); };
    const handleStop = () => scheduler.stop();
    const handleEnded = (e) => { scheduler.stop(); onEnded?.(e); };

    audioEl?.addEventListener("play", handlePlay);
    audioEl?.addEventListener("pause", handleStop);
    audioEl?.addEventListener("ended", handleEnded);

    return () => {
      scheduler.stop();
      audioEl?.removeEventListener("play", handlePlay);
      audioEl?.removeEventListener("pause", handleStop);
      audioEl?.removeEventListener("ended", handleEnded);
    };
  }, [visemes, mouthAssets, onPlay, onEnded]);

  const rect = mouthAssets?.patchRect;

  return (
    <Wrapper data-avatar-id={avatarId}>
      {mouthAssets?.base && <BaseImg src={mouthAssets.base} alt="" draggable={false} />}
      {rect && (
        <>
          <MouthPatch
            src={patchA || mouthAssets?.closed}
            $opacity={layerAActive ? 1 : 0}
            $left={rect.left}
            $top={rect.top}
            $width={rect.width}
            $height={rect.height}
            alt=""
            draggable={false}
          />
          <MouthPatch
            src={patchB || mouthAssets?.closed}
            $opacity={layerAActive ? 0 : 1}
            $left={rect.left}
            $top={rect.top}
            $width={rect.width}
            $height={rect.height}
            alt=""
            draggable={false}
          />
        </>
      )}
      <HiddenAudio
        ref={audioRef}
        src={audioSrc || undefined}
        autoPlay={autoPlay}
        preload={preload}
        onLoadStart={onLoadStart}
        onCanPlay={onCanPlay}
        onLoadedData={onLoadedData}
        onWaiting={onWaiting}
        onError={onError}
      />
    </Wrapper>
  );
});

export default AvatarVisemePlayer;
